/**
 * A very small history router. Routes:
 *   /                     join (code entry)
 *   /join?code=&table=    join with a code (QR codes land here)
 *   /briefing             the hospital briefing (any time, with or without a seat)
 *   /play               the player's phone (seat token from storage)
 *   /control              faculty: sign in, sessions, new session
 *   /control/s/:id        faculty: one session (lobby, room, table, debrief)
 *   /control/s/:id/replay faculty: replay and highlights
 *   /watch/:token         spectator
 *   /projector/:token     projector
 */
import { useEffect, useState } from 'react';

export interface Route {
  path: string;
  parts: string[];
  query: URLSearchParams;
}

function read(): Route {
  const path = location.pathname.replace(/\/+$/, '') || '/';
  return { path, parts: path.split('/').filter(Boolean), query: new URLSearchParams(location.search) };
}

const listeners = new Set<() => void>();

export function navigate(to: string, replace = false): void {
  if (replace) history.replaceState(null, '', to);
  else history.pushState(null, '', to);
  for (const l of listeners) l();
  window.scrollTo(0, 0);
}

export function useRoute(): Route {
  const [route, setRoute] = useState(read);
  useEffect(() => {
    const on = () => setRoute(read());
    listeners.add(on);
    window.addEventListener('popstate', on);
    return () => {
      listeners.delete(on);
      window.removeEventListener('popstate', on);
    };
  }, []);
  return route;
}

/** An <a> that navigates without a reload. */
export function linkProps(to: string): { href: string; onClick: (e: React.MouseEvent) => void } {
  return {
    href: to,
    onClick: (e: React.MouseEvent) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      e.preventDefault();
      navigate(to);
    },
  };
}
